import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FileText, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const inputCls =
  'mt-1.5 w-full rounded-lg border border-line/10 bg-ink/70 px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition focus:border-lime/70 focus:ring-2 focus:ring-lime/15';

function Input({ label, testid, required = true, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">
        {label} {required && <span className="text-lime">*</span>}
      </span>
      <input data-testid={testid} {...props} className={inputCls} />
    </label>
  );
}

function Field({ label, testid, required = true, value, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">
        {label} {required && <span className="text-lime">*</span>}
      </span>
      <textarea data-testid={testid} value={value} {...props} className={`${inputCls} min-h-[90px] resize-y`} />
    </label>
  );
}

function Select({ label, testid, options, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">
        {label} <span className="text-lime">*</span>
      </span>
      <select data-testid={testid} {...props} className={`${inputCls} appearance-none`}>
        {options.map((o) => (
          <option key={o} value={o} className="bg-ink">
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function FileField({ label, hint, testid, accept, isImage, value, onChange }) {
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10 MB)');
      e.target.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', f);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/uploads`, fd);
      onChange(res.data);
      toast.success('File uploaded');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Upload failed. Please try again.');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">{label}</span>
      {value ? (
        <div
          data-testid={`${testid}-preview`}
          className="mt-1.5 flex items-center gap-3 rounded-lg border border-lime/40 bg-ink/60 p-2.5"
        >
          {isImage ? (
            <img
              src={`${API}${value.url}`}
              alt="uploaded"
              className="h-12 w-12 shrink-0 rounded-md border border-line/10 object-cover"
            />
          ) : (
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-line/10 bg-ink-3/70">
              <FileText size={18} className="text-lime" />
            </span>
          )}
          <span className="min-w-0 flex-1 truncate text-xs text-slate-300">{value.filename}</span>
          <button
            type="button"
            data-testid={`${testid}-remove`}
            onClick={() => onChange(null)}
            aria-label="remove file"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line/10 text-slate-400 transition-colors hover:border-lime/60 hover:text-lime"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <label
          className={`mt-1.5 flex cursor-pointer items-center justify-center gap-2.5 rounded-lg border border-dashed border-line/20 bg-ink/40 px-4 py-4 text-xs text-slate-400 transition-all hover:border-lime/50 hover:text-lime ${
            busy ? 'pointer-events-none opacity-60' : ''
          }`}
        >
          <Upload size={15} />
          {busy ? 'Uploading…' : hint}
          <input type="file" accept={accept} onChange={pick} className="hidden" data-testid={testid} />
        </label>
      )}
    </div>
  );
}

const META = {
  register: { title: 'Registration', sub: 'The CXO100 2027 · New Delhi — 05 Feb · Mumbai — 19 Feb', endpoint: 'registrations' },
  winner: { title: 'Join as a Winner', sub: 'The CXO100 Awards 2027', endpoint: 'winners' },
  partner: { title: 'Partnership Opportunity', sub: 'Put your brand at the center of the CXO gathering', endpoint: 'partners' },
};

export default function Modal({ type, onClose }) {
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setForm(type === 'register' ? { intent: 'Join as a Delegate', event_city: 'New Delhi' } : {});
    if (type) {
      document.body.style.overflow = 'hidden';
      const esc = (e) => e.key === 'Escape' && onClose();
      window.addEventListener('keydown', esc);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', esc);
      };
    }
  }, [type, onClose]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const meta = META[type];
    if (!meta) return;
    const payload = { ...form };
    if (payload.doc) {
      payload.doc_path = payload.doc.path;
      delete payload.doc;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/${meta.endpoint}`, payload);
      toast.success(res.data?.message || 'Submitted successfully');
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(', ')
        : detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const meta = type ? META[type] : null;

  return (
    <AnimatePresence>
      {type && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="modal-backdrop"
        >
          <div className="absolute inset-0 bg-ink/80 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-lime/25 bg-ink-2 p-8 shadow-[0_0_80px_rgba(153,204,0,0.12)]"
            data-testid={`${type}-modal`}
          >
            <button
              data-testid={`${type}-modal-close`}
              onClick={onClose}
              aria-label="close"
              className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full border border-line/10 text-slate-400 transition-all hover:border-lime/60 hover:text-lime"
            >
              <X size={15} />
            </button>
            <p className="pr-10 font-mono text-[10px] uppercase tracking-[0.3em] text-lime">{meta.sub}</p>
            <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">{meta.title}</h3>

            <form onSubmit={submit} className="mt-7 space-y-4" data-testid={`${type}-form`}>
              {type === 'register' && (
                <>
                  <Select
                    label="I want to"
                    testid="reg-intent-select"
                    options={['Join as a Delegate', 'Apply to be a Speaker', 'Partnership Opportunity']}
                    value={form.intent || 'Join as a Delegate'}
                    onChange={set('intent')}
                  />
                  <label className="block">
                    <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-400">
                      Select Event City <span className="text-lime">*</span>
                    </span>
                    <select
                      data-testid="reg-event-city-select"
                      value={form.event_city || 'New Delhi'}
                      onChange={set('event_city')}
                      className={`${inputCls} appearance-none`}
                    >
                      <option value="New Delhi" className="bg-ink">New Delhi — 05 Feb, 2027</option>
                      <option value="Mumbai" className="bg-ink">Mumbai — 19 Feb, 2027</option>
                    </select>
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="First Name" testid="reg-firstname-input" required value={form.first_name || ''} onChange={set('first_name')} placeholder="First name" />
                    <Input label="Last Name" testid="reg-lastname-input" required value={form.last_name || ''} onChange={set('last_name')} placeholder="Last name" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Organisation" testid="reg-organisation-input" required value={form.organisation || ''} onChange={set('organisation')} placeholder="Company" />
                    <Input label="Designation" testid="reg-designation-input" required value={form.designation || ''} onChange={set('designation')} placeholder="Job title" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Email" testid="reg-email-input" type="email" required value={form.email || ''} onChange={set('email')} placeholder="you@company.com" />
                    <Input label="Mobile No" testid="reg-mobile-input" required value={form.mobile || ''} onChange={set('mobile')} placeholder="+91" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="City" testid="reg-city-input" required value={form.city || ''} onChange={set('city')} placeholder="City" />
                    <Input label="Pin" testid="reg-pin-input" required value={form.pin || ''} onChange={set('pin')} placeholder="PIN code" />
                  </div>
                  <Input label="Industry" testid="reg-industry-input" required value={form.industry || ''} onChange={set('industry')} placeholder="Industry" />
                  <Input label="Linkedin" testid="reg-linkedin-input" required={false} value={form.linkedin || ''} onChange={set('linkedin')} placeholder="https://linkedin.com/in/..." />
                </>
              )}

              {type === 'winner' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="First Name" testid="winner-firstname-input" required value={form.first_name || ''} onChange={set('first_name')} placeholder="First name" />
                    <Input label="Last Name" testid="winner-lastname-input" required value={form.last_name || ''} onChange={set('last_name')} placeholder="Last name" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Organisation" testid="winner-organisation-input" required value={form.organisation || ''} onChange={set('organisation')} placeholder="Company" />
                    <Input label="Designation" testid="winner-designation-input" required value={form.designation || ''} onChange={set('designation')} placeholder="Job title" />
                  </div>
                  <Input label="Official Email" testid="winner-email-input" type="email" required value={form.email || ''} onChange={set('email')} placeholder="you@company.com" />
                  <Input label="Mobile No" testid="winner-mobile-input" required value={form.mobile || ''} onChange={set('mobile')} placeholder="+91" />
                </>
              )}

              {type === 'partner' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="First Name" testid="partner-firstname-input" required value={form.first_name || ''} onChange={set('first_name')} placeholder="First name" />
                    <Input label="Last Name" testid="partner-lastname-input" required value={form.last_name || ''} onChange={set('last_name')} placeholder="Last name" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Company" testid="partner-company-input" required value={form.company || ''} onChange={set('company')} placeholder="Company" />
                    <Input label="Designation" testid="partner-designation-input" required value={form.designation || ''} onChange={set('designation')} placeholder="Job title" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Email" testid="partner-email-input" type="email" required value={form.email || ''} onChange={set('email')} placeholder="you@company.com" />
                    <Input label="Mobile No" testid="partner-mobile-input" required={false} value={form.mobile || ''} onChange={set('mobile')} placeholder="+91" />
                  </div>
                  <Field
                    label="Message"
                    testid="partner-message-input"
                    value={form.message || ''}
                    onChange={set('message')}
                    placeholder="Tell us about your partnership goals"
                  />
                  <FileField
                    label="Company Profile (optional)"
                    hint="Upload document — pdf, doc, docx or ppt, max 10 MB"
                    testid="partner-doc-input"
                    accept=".pdf,.doc,.docx,.ppt,.pptx"
                    isImage={false}
                    value={form.doc || null}
                    onChange={(v) => setForm((f) => ({ ...f, doc: v }))}
                  />
                </>
              )}

              <label className="flex items-start gap-3 pt-1 text-xs leading-relaxed text-slate-400">
                <input
                  type="checkbox"
                  data-testid={`${type}-consent-checkbox`}
                  checked={form.consent !== false}
                  onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-lime"
                />
                I agree to my contact data being passed to sponsors to reach out to me.
              </label>

              <button
                type="submit"
                data-testid={`${type}-modal-submit`}
                disabled={sending}
                className="w-full rounded-full bg-lime py-4 font-display text-sm font-bold uppercase tracking-widest text-ink transition-all duration-300 hover:shadow-[0_0_36px_rgba(153,204,0,0.45)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? 'Submitting…' : 'Submit'}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
